import type {Route} from '@/types/route';

export interface RouteComparison {
  advantage: string;
  distanceDifference: number;
  durationDifference: number;
  isShortestDistance: boolean;
  isFastestTime: boolean;
}

export interface RouteStats {
  distance: number;
  duration: number;
  staticDuration?: number;
}

/**
 * Service for comparing routes and calculating route advantages
 */
export class RouteComparator {
    private static readonly SIGNIFICANT_DISTANCE_DIFF = 5000; // 5km in meters
    private static readonly SIGNIFICANT_DURATION_DIFF = 300; // 5 minutes in seconds

    /**
   * Compare an alternative route against a primary route
   * @param {Route} primaryRoute - The primary route to compare against
   * @param {Route} alternativeRoute - The alternative route to compare
   * @return {RouteComparison} Comparison results including advantages and differences
   */
    static compareRoute(primaryRoute: Route, alternativeRoute: Route): RouteComparison {
        const primaryStats = this.extractRouteStats(primaryRoute);
        const altStats = this.extractRouteStats(alternativeRoute);

        const distanceDiff = primaryStats.distance - altStats.distance;
        const durationDiff = primaryStats.duration - altStats.duration;

        return {
            advantage: this.determineAdvantage(distanceDiff, durationDiff),
            distanceDifference: distanceDiff,
            durationDifference: durationDiff,
            isShortestDistance: distanceDiff > 0,
            isFastestTime: durationDiff > 0,
        };
    }

    /**
   * Find the best route among multiple routes based on different criteria
   * @param {Route[]} routes - Array of routes to compare
   * @param {'distance' | 'time'} criteria - Criteria for comparison (distance or time)
   * @return {Route | null} The best route or null if no routes provided
   */
    static findBestRoute(routes: Route[], criteria: 'distance' | 'time' = 'time'): Route | null {
        if (routes.length === 0) return null;

        return routes.reduce((best, current) => {
            const bestStats = this.extractRouteStats(best);
            const currentStats = this.extractRouteStats(current);

            if (criteria === 'distance') {
                return currentStats.distance < bestStats.distance ? current : best;
            } else {
                return currentStats.duration < bestStats.duration ? current : best;
            }
        });
    }

    /**
   * Rank routes by multiple criteria
   * @param {Route[]} routes - Array of routes to rank
   * @return {Route[]} Sorted array of routes ranked by efficiency
   */
    static rankRoutes(routes: Route[]): Route[] {
        return [...routes].sort((a, b) => {
            const aStats = this.extractRouteStats(a);
            const bStats = this.extractRouteStats(b);

            // Primary: fastest time
            const timeDiff = aStats.duration - bStats.duration;
            if (Math.abs(timeDiff) > this.SIGNIFICANT_DURATION_DIFF) {
                return timeDiff;
            }

            // Secondary: shortest distance
            return aStats.distance - bStats.distance;
        });
    }

    /**
   * Calculate efficiency score for a route (lower is better)
   * @param {Route} route - The route to calculate efficiency for
   * @return {number} Efficiency score (time per km, lower is better)
   */
    static calculateEfficiencyScore(route: Route): number {
        const stats = this.extractRouteStats(route);

        // Normalize distance (meters to km) and duration (seconds to minutes)
        const distanceKm = stats.distance / 1000;
        const durationMinutes = stats.duration / 60;

        // Simple efficiency score: time per km
        return durationMinutes / distanceKm;
    }

    /**
   * Get route comparison statistics for multiple routes
   * @param {Route[]} routes - Array of routes to analyze
   * @return {object | null} Statistics object or null if no routes provided
   */
    static getRouteComparisonStats(routes: Route[]) {
        if (routes.length === 0) return null;

        const stats = routes.map((route) => this.extractRouteStats(route));

        const distances = stats.map((s) => s.distance);
        const durations = stats.map((s) => s.duration);

        return {
            averageDistance: distances.reduce((sum, d) => sum + d, 0) / distances.length,
            averageDuration: durations.reduce((sum, d) => sum + d, 0) / durations.length,
            shortestDistance: Math.min(...distances),
            longestDistance: Math.max(...distances),
            fastestTime: Math.min(...durations),
            slowestTime: Math.max(...durations),
            distanceRange: Math.max(...distances) - Math.min(...distances),
            timeRange: Math.max(...durations) - Math.min(...durations),
        };
    }

    /**
   * Extract standardized stats from a route object
   * @param {Route} route - The route to extract stats from
   * @return {RouteStats} Standardized route statistics
   */
    private static extractRouteStats(route: Route): RouteStats {
        return {
            distance: route.distanceMeters,
            duration: parseInt(route.duration.replace('s', '')),
            staticDuration: route.staticDuration ? parseInt(route.staticDuration.replace('s', '')) : undefined,
        };
    }

    /**
   * Determine the advantage string for a route comparison
   * @param {number} distanceDiff - Distance difference in meters
   * @param {number} durationDiff - Duration difference in seconds
   * @return {string} Advantage description (Shorter, Faster, etc.)
   */
    private static determineAdvantage(distanceDiff: number, durationDiff: number): string {
    // Check for significant distance difference first
        if (Math.abs(distanceDiff) > this.SIGNIFICANT_DISTANCE_DIFF) {
            return distanceDiff > 0 ? 'Shorter' : 'Longer';
        }

        // Check for significant duration difference
        if (Math.abs(durationDiff) > this.SIGNIFICANT_DURATION_DIFF) {
            return durationDiff > 0 ? 'Faster' : 'Slower';
        }

        // No significant advantage
        return '';
    }
}
